import { supabaseStorageCompat as supabase } from "./fileStorage";

import { db } from "./dbClient";

/*
  SQL to run in Supabase:

  CREATE TABLE IF NOT EXISTS chat_messages (
    id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
    company_id uuid NOT NULL,
    channel text NOT NULL DEFAULT 'general',
    sender_id text NOT NULL,
    sender_name text NOT NULL,
    sender_initials text,
    message text NOT NULL,
    mentions jsonb,
    created_at timestamptz DEFAULT now()
  );
  CREATE INDEX ON chat_messages (company_id, channel, created_at);

  -- Add mentions column if table already exists:
  -- ALTER TABLE chat_messages ADD COLUMN IF NOT EXISTS mentions jsonb;

  CREATE TABLE IF NOT EXISTS chat_channels (
    id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
    company_id uuid NOT NULL,
    name text NOT NULL,
    created_by text,
    created_at timestamptz DEFAULT now()
  );

  -- Notifications table for team chat and system alerts
  CREATE TABLE IF NOT EXISTS notifications (
    id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
    user_id text NOT NULL,
    company_id uuid,
    type text DEFAULT 'message',
    title text NOT NULL,
    body text,
    link_module text,
    read boolean DEFAULT false,
    created_at timestamptz DEFAULT now()
  );
  CREATE INDEX ON notifications (user_id, created_at);
*/

// ─── TeamChat.jsx ─────────────────────────────────────────────────────────────
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";

// Note: TeamChat uses localStorage for persistence since Supabase was removed
// For production, integrate with your Neon database via API endpoints

const HARDCODED_CHANNELS = ["general", "alerts", "sar-filing", "ctr-filing", "investigations"];

const EMOJI_LIST = ["👍", "✅", "❌", "⚠️", "🔍", "📋", "💡", "🚨", "👀"];

const CHAR_LIMIT = 2000;

function getInitials(name = "") {
  return name
    .split(" ")
    .map((p) => p[0] || "")
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function avatarGradient(name = "") {
  const colors = [
    ["#00D4FF", "#0099BB"],
    ["#A78BFA", "#6D28D9"],
    ["#00E676", "#047857"],
    ["#FF8C00", "#C46A00"],
    ["#F0B429", "#92400E"],
    ["#FF6B35", "#C2410C"],
    ["#FF4444", "#CC2222"],
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const [a, b] = colors[Math.abs(hash) % colors.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

function formatTime(ts) {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function dateSeparatorLabel(ts) {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
}

function groupMessagesByDate(messages) {
  const groups = [];
  let lastLabel = null;
  for (const msg of messages) {
    const label = dateSeparatorLabel(msg.created_at);
    if (label !== lastLabel) {
      groups.push({ type: "separator", label, key: "sep-" + msg.id });
      lastLabel = label;
    }
    groups.push({ type: "message", ...msg, key: msg.id });
  }
  return groups;
}

// ─── Main Component ───────────────────────────────────────────────────────────
// Item 15: accepts initialCaseRef to pre-populate message with case context
// #25: accepts onNavigate to enable case tag click-through navigation
// Can accept initialDmPerson to start a DM with a specific team member
export default function TeamChat({ currentUser, company, initialCaseRef, initialMessage, onNavigate, initialDmPerson }) {
  const C = useTheme();
  const { t } = useTranslation();

  const [channels, setChannels] = useState(HARDCODED_CHANNELS);
  const [activeChannel, setActiveChannel] = useState("general");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputText, setInputText] = useState(initialMessage || "");
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [unread, setUnread] = useState({});
  const [lastVisited, setLastVisited] = useState({});
  // DM state
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeDmPerson, setActiveDmPerson] = useState(null);
  // @mention state
  const [mentionQuery, setMentionQuery] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  // #25: Case thread filter
  const [caseFilter, setCaseFilter] = useState(initialCaseRef || "");

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const subscriptionRef = useRef(null);
  const emojiRef = useRef(null);
  const mentionRef = useRef(null);

  // ── Load team members for DMs ────────────────────────────────────────────────
  useEffect(() => {
    if (!company?.id) return;
    db?.from("company_members").select("id,email")
      .eq("company_id", company.id)
      .then(({ data, error }) => {
        if (error || !data) {
          console.warn('Team members load skipped:', error?.message || 'No data');
          return;
        }
        const myEmail = currentUser?.email;
        setTeamMembers(data.filter(m => m.email !== myEmail).map(m => ({
          id: m.id || m.email,
          email: m.email,
          name: m.email.split("@")[0] || m.email,
          role: "",
        })));
      })
      .catch(err => console.warn('Team members load skipped', err));
  }, [company?.id, currentUser?.email]);

  // ── Set initial DM person if provided ─────────────────────────────────────────
  useEffect(() => {
    if (initialDmPerson) {
      setActiveDmPerson({
        id: initialDmPerson.id || initialDmPerson.email,
        email: initialDmPerson.email,
        name: [initialDmPerson.firstName, initialDmPerson.lastName].filter(Boolean).join(" ") || initialDmPerson.email,
        role: initialDmPerson.role || "",
      });
    }
  }, [initialDmPerson?.id]);

  // ── DM channel key (deterministic, sorted) ───────────────────────────────────
  function getDMChannelId(otherPerson) {
    const myId2 = currentUser?.id || currentUser?.email || "";
    const theirId = otherPerson.id || otherPerson.email || "";
    return "dm_" + [myId2, theirId].sort().join("__");
  }

  function openDM(person) {
    const ch = getDMChannelId(person);
    setActiveDmPerson(person);
    setActiveChannel(ch);
    markVisited(ch);
  }

  // ── Load custom channels from Neon ────────────────────────────────────────
  useEffect(() => {
    if (!company?.id) return;
    db?.from("company_channels")
      .select("id,name,description").eq("company_id", company.id)
      .order("created_at", true)
      .then(({ data, error }) => {
        if (error) {
          console.warn('[TeamChat] Custom channels load error:', error.message);
          return;
        }
        if (data?.length) {
          const custom = data.map(r => r.name).filter(n => !HARDCODED_CHANNELS.includes(n));
          setChannels([...HARDCODED_CHANNELS, ...custom]);
        }
      })
      .catch(err => console.warn('[TeamChat] Custom channels load failed:', err));
  }, [company?.id]);

  // ── Load last-visited timestamps from localStorage ──────────────────────────
  useEffect(() => {
    if (!company?.id || !currentUser?.id) return;
    try {
      const key = `chat_visited_${company.id}_${currentUser.id}`;
      const stored = localStorage.getItem(key);
      if (stored) setLastVisited(JSON.parse(stored));
    } catch {}
  }, [company?.id, currentUser?.id]);

  // ── Mark channel as visited when switching ──────────────────────────────────
  const markVisited = useCallback(
    (channel) => {
      if (!company?.id || !currentUser?.id) return;
      const now = new Date().toISOString();
      setLastVisited((prev) => {
        const next = { ...prev, [channel]: now };
        try {
          localStorage.setItem(`chat_visited_${company.id}_${currentUser.id}`, JSON.stringify(next));
        } catch {}
        return next;
      });
      setUnread((prev) => ({ ...prev, [channel]: 0 }));
    },
    [company?.id, currentUser?.id]
  );

  // ── Fetch messages for active channel ───────────────────────────────────────
  useEffect(() => {
    if (!company?.id) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setMessages([]);

    db?.from("chat_messages")
      .select("*")
      .eq("company_id", company.id)
      .eq("channel", activeChannel)
      .order("created_at", true)
      .limit(100)
      .then(({ data, error }) => {
        if (error) {
          console.warn("[TeamChat] load error:", error.message);
          setMessages([]);
        } else {
          setMessages(data || []);
        }
        setLoading(false);
        markVisited(activeChannel);
      })
      .catch(err => {
        console.warn("[TeamChat] messages fetch failed:", err);
        setMessages([]);
        setLoading(false);
      });
  }, [company?.id, activeChannel, markVisited]);

  // ── Polling for new messages (real-time fallback) ────────────────────────────
  useEffect(() => {
    if (!company?.id) return;

    let pollInterval;

    // Initial fetch is already done, now poll for new messages
    const pollMessages = async () => {
      try {
        const { data, error } = await db?.from("chat_messages")
          .select("*")
          .eq("company_id", company.id)
          .eq("channel", activeChannel)
          .order("created_at", true)
          .limit(100);

        if (!error && data) {
          // Only update if there are new messages or changes
          setMessages((prev) => {
            const prevIds = new Set(prev.map(m => m.id));
            const hasNew = data.some(m => !prevIds.has(m.id));
            if (hasNew) {
              console.log("[TeamChat] New messages received via polling");
              return data;
            }
            return prev;
          });
        }
      } catch (err) {
        console.warn("[TeamChat] Polling error:", err?.message);
      }
    };

    // Start polling every 2 seconds
    pollInterval = setInterval(pollMessages, 2000);

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [company?.id, activeChannel]);

  // ── Auto-scroll to bottom on new messages ───────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Close emoji picker on outside click ─────────────────────────────────────
  useEffect(() => {
    if (!showEmoji) return;
    function handleClick(e) {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) {
        setShowEmoji(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showEmoji]);

  // ── Detect @mentions in input ──────────────────────────────────────────────────
  useEffect(() => {
    const text = inputText;
    const atIndex = text.lastIndexOf("@");
    if (atIndex === -1) {
      setShowMentions(false);
      return;
    }

    // Check if @ is at word boundary (preceded by space or start)
    if (atIndex > 0 && !/\s/.test(text[atIndex - 1])) {
      setShowMentions(false);
      return;
    }

    const afterAt = text.substring(atIndex + 1);
    if (/\s/.test(afterAt)) {
      setShowMentions(false);
      return;
    }

    const query = afterAt.toLowerCase();
    setMentionQuery(query);

    if (query.length === 0) {
      setMentionSuggestions(teamMembers);
    } else {
      const filtered = teamMembers.filter(m =>
        m.name.toLowerCase().includes(query) ||
        m.email.toLowerCase().includes(query)
      );
      setMentionSuggestions(filtered);
    }

    setShowMentions(true);
  }, [inputText, teamMembers]);

  // ── Insert mention ─────────────────────────────────────────────────────────────
  function insertMention(member) {
    const text = inputText;
    const atIndex = text.lastIndexOf("@");
    const beforeAt = text.substring(0, atIndex);
    const afterAt = text.substring(atIndex + 1 + mentionQuery.length);
    const newText = beforeAt + "@" + member.name + " " + afterAt;
    setInputText(newText);
    setShowMentions(false);
    textareaRef.current?.focus();
  }

  // ── Auto-resize textarea ─────────────────────────────────────────────────────
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    const lineH = 22;
    const maxH = lineH * 4 + 16;
    ta.style.height = Math.min(ta.scrollHeight, maxH) + "px";
  }, [inputText]);

  // ── Parse @mentions from text ──────────────────────────────────────────────────
  function parseMentions(text) {
    const mentionRegex = /@(\w+(?:\s\w+)*)/g;
    const mentions = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      const mentionedName = match[1];
      const member = teamMembers.find(m => m.name === mentionedName);
      if (member) {
        mentions.push({
          name: mentionedName,
          email: member.email,
        });
      }
    }
    return mentions;
  }

  // ── Send message ─────────────────────────────────────────────────────────────
  function handleSend() {
    const text = inputText.trim();
    if (!text || sending || !company?.id || !currentUser) return;

    const mentions = parseMentions(text);
    const senderName = currentUser.full_name || currentUser.email || "User";

    // Optimistic update: add message immediately to UI
    const optimisticMsg = {
      id: "temp-" + Date.now(),
      company_id: company.id,
      channel: activeChannel,
      user_id: currentUser.id || currentUser.email,
      sender_name: senderName,
      sender_initials: getInitials(senderName),
      message: text,
      mentions: mentions.length > 0 ? JSON.stringify(mentions) : null,
      created_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, optimisticMsg]);
    setSending(true);

    db?.from("chat_messages")
      .insert({
        company_id: company.id,
        channel: activeChannel,
        user_id: currentUser.id || currentUser.email,
        sender_name: senderName,
        sender_initials: getInitials(senderName),
        message: text,
        mentions: mentions.length > 0 ? JSON.stringify(mentions) : null,
      })
      .then(({ data, error }) => {
        if (error) {
          console.warn("[TeamChat] send error:", error.message);
          // Remove optimistic message on error
          setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
        } else if (data && data.length > 0) {
          const newMsg = data[0];
          // Replace optimistic message with real one
          setMessages(prev => prev.map(m => m.id === optimisticMsg.id ? newMsg : m));

          // Create notifications for all team members and mentioned users
          createNotificationsForMessage(newMsg, mentions);
        }
      })
      .catch(err => {
        console.warn("[TeamChat] send failed:", err.message);
        // Remove optimistic message on error
        setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
      })
      .finally(() => {
        setInputText("");
        setSending(false);
        textareaRef.current?.focus();
      });
  }

  // ── Create notifications for team members ────────────────────────────────────
  async function createNotificationsForMessage(msg, mentions = []) {
    if (!company?.id) return;

    try {
      const myEmail = currentUser?.email;
      const senderName = currentUser?.full_name || currentUser?.email || "User";
      const messagePreview = msg.message.substring(0, 60) + (msg.message.length > 60 ? "..." : "");

      // Check if this is a direct message
      const isDM = activeChannel.startsWith("dm_");

      if (isDM) {
        // For DMs, extract recipient email and notify only them
        const parts = activeChannel.substring(3).split("__");
        const recipientEmail = parts.find(p => p !== myEmail);

        if (!recipientEmail) return;

        const notificationTitle = `${senderName} sent you a message`;
        const notificationBody = messagePreview;

        try {
          await db?.from("notifications")
            .insert({
              user_id: recipientEmail,
              company_id: company.id,
              type: "chat_message",
              title: notificationTitle,
              body: notificationBody,
              link_module: "team-chat",
            });
          console.log(`[TeamChat] DM notification sent to ${recipientEmail}`);
        } catch (err) {
          console.warn(`[TeamChat] Failed to notify ${recipientEmail}:`, err.message?.substring(0, 100));
        }
      } else {
        // For channels, prioritize notifying mentioned users, then all team members
        const mentionedEmails = mentions.map(m => m.email);

        // If there are mentions, notify only them
        if (mentionedEmails.length > 0) {
          const notificationTitle = `${senderName} mentioned you in #${activeChannel}`;

          for (const email of mentionedEmails) {
            if (email === myEmail) continue; // Don't notify the sender

            try {
              await db?.from("notifications")
                .insert({
                  user_id: email,
                  company_id: company.id,
                  type: "chat_message",
                  title: notificationTitle,
                  body: messagePreview,
                  link_module: "team-chat",
                });
            } catch (err) {
              console.warn(`[TeamChat] Failed to notify ${email}:`, err.message?.substring(0, 100));
            }
          }
          console.log(`[TeamChat] Mention notifications sent in #${activeChannel}`);
        } else {
          // No mentions: notify all team members except sender
          const { data: members, error: memberError } = await db?.from("company_members")
            .select("id,email,full_name")
            .eq("company_id", company.id);

          if (memberError || !members) {
            console.warn("[TeamChat] Error loading team members:", memberError?.message);
            return;
          }

          const notificationTitle = `${senderName} posted in #${activeChannel}`;

          for (const member of members) {
            // Don't notify the sender
            if (member.email === myEmail) continue;

            try {
              await db?.from("notifications")
                .insert({
                  user_id: member.email,
                  company_id: company.id,
                  type: "chat_message",
                  title: notificationTitle,
                  body: messagePreview,
                  link_module: "team-chat",
                });
            } catch (err) {
              console.warn(`[TeamChat] Failed to notify ${member.email}:`, err.message?.substring(0, 100));
            }
          }

          console.log(`[TeamChat] Notifications sent for message in #${activeChannel}`);
        }
      }
    } catch (err) {
      console.warn("[TeamChat] Error creating notifications:", err.message);
    }
  }

  function handleKeyDown(e) {
    // If mention dropdown is showing, handle arrow keys
    if (showMentions && mentionSuggestions.length > 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        // Could implement mention selection cycling here
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // ── Render message text with highlighted mentions ────────────────────────────
  function renderMessageWithMentions(text) {
    const parts = text.split(/(@\w+(?:\s\w+)*)/);
    return parts.map((part, i) => {
      if (part.startsWith("@")) {
        return (
          <span key={i} style={{ background: C.accentGlow, color: C.accent, borderRadius: 3, padding: "1px 4px", fontWeight: 500 }}>
            {part}
          </span>
        );
      }
      return part;
    });
  }

  // ── Add custom channel ────────────────────────────────────────────────────────
  function handleAddChannel() {
    const raw = window.prompt(t('teamChat.newChannelPrompt'));
    if (!raw) return;
    const name = raw.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    if (!name || channels.includes(name)) return;
    if (company?.id) {
      db?.from("company_channels")
        .insert({ company_id: company.id, name })
        .then(({ error }) => {
          if (error) {
            console.warn("[TeamChat] create channel error:", error.message);
            alert(t('teamChat.createChannelError'));
          } else {
            setChannels(prev => [...prev, name]);
            setActiveChannel(name);
          }
        })
        .catch(err => {
          console.warn("[TeamChat] create channel failed:", err.message);
          alert(t('teamChat.createChannelError'));
        });
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const myId = currentUser?.id || currentUser?.email;
  const filteredMessages = caseFilter.trim()
    ? messages.filter(m => m.message?.toLowerCase().includes(caseFilter.trim().toLowerCase()))
    : messages;
  const grouped = groupMessagesByDate(filteredMessages);
  const charsLeft = CHAR_LIMIT - inputText.length;

  // ── Styles ───────────────────────────────────────────────────────────────────
  const S = {
    root: {
      display: "flex",
      height: "calc(100vh - 80px)",
      background: C.bg,
      fontFamily: "'DM Mono', monospace",
      color: C.text,
      borderRadius: 12,
      overflow: "hidden",
      border: `1px solid ${C.border}`,
    },
    sidebar: {
      width: 200,
      minWidth: 200,
      display: "flex",
      flexDirection: "column",
      background: C.surface,
      borderRight: `1px solid ${C.border}`,
    },
    sidebarHeader: {
      padding: "16px 14px 10px",
      borderBottom: `1px solid ${C.border}`,
    },
    sidebarTitle: {
      fontFamily: "'Syne', sans-serif",
      fontSize: 13,
      fontWeight: 700,
      color: C.textMid,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      margin: 0,
    },
    channelList: {
      flex: 1,
      overflowY: "auto",
      padding: "8px 0",
    },
    channelItem: (isActive) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "7px 14px",
      cursor: "pointer",
      borderRadius: 6,
      margin: "1px 6px",
      background: isActive ? C.accentGlow : "transparent",
      color: isActive ? C.accent : C.textMid,
      fontSize: 13,
      fontWeight: isActive ? 600 : 400,
      transition: "background 0.15s, color 0.15s",
    }),
    channelName: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    unreadBadge: {
      background: C.accent,
      color: C.bg,
      borderRadius: 10,
      fontSize: 10,
      fontWeight: 700,
      minWidth: 18,
      height: 18,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 5px",
      flexShrink: 0,
      fontFamily: "'DM Mono', monospace",
    },
    addChannelBtn: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      margin: "8px 6px 12px",
      padding: "7px 14px",
      background: "transparent",
      border: `1px dashed ${C.borderLight}`,
      borderRadius: 6,
      color: C.textDim,
      fontSize: 12,
      cursor: "pointer",
      fontFamily: "'DM Mono', monospace",
      transition: "color 0.15s, border-color 0.15s",
    },
    main: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      background: C.bg,
      minWidth: 0,
    },
    channelHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 20px",
      borderBottom: `1px solid ${C.border}`,
      background: C.surface,
      flexShrink: 0,
    },
    channelHeaderLeft: {
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    channelHashIcon: {
      fontFamily: "'Syne', sans-serif",
      fontSize: 20,
      fontWeight: 700,
      color: C.accent,
      lineHeight: 1,
    },
    channelHeaderName: {
      fontFamily: "'Syne', sans-serif",
      fontSize: 16,
      fontWeight: 700,
      color: C.text,
      margin: 0,
    },
    channelHeaderMeta: {
      fontSize: 12,
      color: C.textDim,
      fontFamily: "'DM Mono', monospace",
    },
    messageArea: {
      flex: 1,
      overflowY: "auto",
      padding: "16px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 0,
    },
    separator: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      margin: "18px 0 10px",
    },
    separatorLine: {
      flex: 1,
      height: 1,
      background: C.border,
    },
    separatorLabel: {
      fontSize: 11,
      color: C.textDim,
      fontFamily: "'DM Mono', monospace",
      whiteSpace: "nowrap",
      letterSpacing: "0.05em",
    },
    msgRow: (isMine) => ({
      display: "flex",
      flexDirection: isMine ? "row-reverse" : "row",
      alignItems: "flex-end",
      gap: 8,
      marginBottom: 10,
    }),
    avatar: (name) => ({
      width: 32,
      height: 32,
      borderRadius: "50%",
      background: avatarGradient(name),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 700,
      color: "#fff",
      flexShrink: 0,
      fontFamily: "'DM Mono', monospace",
      letterSpacing: "0.02em",
    }),
    msgBubbleWrap: (isMine) => ({
      display: "flex",
      flexDirection: "column",
      alignItems: isMine ? "flex-end" : "flex-start",
      maxWidth: "65%",
    }),
    msgMeta: (isMine) => ({
      display: "flex",
      alignItems: "baseline",
      gap: 6,
      marginBottom: 3,
      flexDirection: isMine ? "row-reverse" : "row",
    }),
    msgSender: {
      fontSize: 12,
      fontWeight: 600,
      color: C.textMid,
      fontFamily: "'DM Mono', monospace",
    },
    msgTime: {
      fontSize: 10,
      color: C.textDim,
      fontFamily: "'DM Mono', monospace",
    },
    msgBubble: (isMine) => ({
      background: isMine ? `rgba(0,212,255,0.13)` : C.surfaceAlt,
      border: `1px solid ${isMine ? "rgba(0,212,255,0.25)" : C.border}`,
      borderRadius: isMine ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
      padding: "9px 13px",
      fontSize: 13,
      lineHeight: 1.55,
      color: C.text,
      wordBreak: "break-word",
      whiteSpace: "pre-wrap",
      fontFamily: "'DM Mono', monospace",
    }),
    emptyState: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      color: C.textDim,
      gap: 10,
      fontFamily: "'DM Mono', monospace",
    },
    emptyIcon: {
      fontSize: 36,
      opacity: 0.4,
    },
    emptyText: {
      fontSize: 13,
      color: C.textDim,
    },
    loadingWrap: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: C.textDim,
      fontSize: 13,
      fontFamily: "'DM Mono', monospace",
      gap: 8,
    },
    inputArea: {
      padding: "12px 16px",
      borderTop: `1px solid ${C.border}`,
      background: C.surface,
      flexShrink: 0,
    },
    inputRow: {
      display: "flex",
      alignItems: "flex-end",
      gap: 8,
      background: C.surfaceAlt,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: "8px 10px",
      transition: "border-color 0.15s",
    },
    textarea: {
      flex: 1,
      background: "transparent",
      border: "none",
      outline: "none",
      resize: "none",
      color: C.text,
      fontSize: 13,
      fontFamily: "'DM Mono', monospace",
      lineHeight: "22px",
      minHeight: 22,
      maxHeight: 22 * 4 + 16,
      padding: 0,
      caretColor: C.accent,
    },
    emojiBtn: {
      background: "transparent",
      border: "none",
      cursor: "pointer",
      fontSize: 18,
      padding: "2px 4px",
      borderRadius: 6,
      color: C.textDim,
      transition: "color 0.15s",
      flexShrink: 0,
      lineHeight: 1,
    },
    sendBtn: (disabled) => ({
      background: disabled ? C.border : C.accent,
      border: "none",
      borderRadius: 8,
      cursor: disabled ? "not-allowed" : "pointer",
      color: disabled ? C.textDim : C.bg,
      width: 32,
      height: 32,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      transition: "background 0.15s",
      fontFamily: "'DM Mono', monospace",
    }),
    inputMeta: {
      display: "flex",
      justifyContent: "space-between",
      marginTop: 5,
      padding: "0 4px",
    },
    inputHint: {
      fontSize: 10,
      color: C.textDim,
      fontFamily: "'DM Mono', monospace",
    },
    charCounter: (warn) => ({
      fontSize: 10,
      color: warn ? C.warn : C.textDim,
      fontFamily: "'DM Mono', monospace",
    }),
    emojiPopover: {
      position: "absolute",
      bottom: "100%",
      right: 0,
      marginBottom: 6,
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: 10,
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      width: 190,
      boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
      zIndex: 100,
    },
    emojiOption: {
      fontSize: 22,
      cursor: "pointer",
      borderRadius: 6,
      padding: "3px 5px",
      transition: "background 0.1s",
      lineHeight: 1,
      background: "transparent",
      border: "none",
    },
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={S.root}>
      {/* ── Channel Sidebar ── */}
      <aside style={S.sidebar}>
        <div style={S.sidebarHeader}>
          <p style={S.sidebarTitle}>{t('teamChat.channelsHeading')}</p>
        </div>
        <div style={S.channelList}>
          {channels.map((ch) => {
            const isActive = ch === activeChannel;
            const count = unread[ch] || 0;
            return (
              <div
                key={ch}
                style={S.channelItem(isActive)}
                onClick={() => {
                  setActiveChannel(ch);
                  setActiveDmPerson(null);
                  markVisited(ch);
                }}
              >
                <span style={S.channelName}>
                  <span style={{ opacity: 0.5, marginRight: 2 }}>#</span>
                  {ch}
                </span>
                {count > 0 && !isActive && (
                  <span style={S.unreadBadge}>{count > 99 ? "99+" : count}</span>
                )}
              </div>
            );
          })}
        </div>
        <button
          style={S.addChannelBtn}
          onClick={handleAddChannel}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = C.textMid;
            e.currentTarget.style.borderColor = C.borderLight;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = C.textDim;
            e.currentTarget.style.borderColor = C.borderLight;
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
          {t('teamChat.addChannel')}
        </button>

        {/* Direct Messages section */}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, paddingBottom: 8 }}>
          <div style={{ padding: "4px 14px 6px", color: C.textDim, fontSize: 9,
            letterSpacing: "0.14em", fontFamily: "'DM Mono',monospace" }}>
            {t('teamChat.directMessagesHeading')}
          </div>
          {teamMembers.length === 0 ? (
            <div style={{ padding: "4px 14px", color: C.textDim, fontSize: 11 }}>
              {t('teamChat.noTeammatesYet')}
            </div>
          ) : teamMembers.map(person => {
            const dmCh = getDMChannelId(person);
            const isActive = activeChannel === dmCh;
            const dmUnread = unread[dmCh] || 0;
            const initials = getInitials(person.name);
            return (
              <div key={person.id} style={{
                ...S.channelItem(isActive),
                gap: 8,
              }} onClick={() => openDM(person)}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%",
                  background: avatarGradient(person.name),
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, fontWeight: 700, color: "#fff", flexShrink: 0,
                }}>
                  {initials}
                </div>
                <span style={{ ...S.channelName, flex: 1 }}>{person.name}</span>
                {dmUnread > 0 && !isActive && (
                  <span style={S.unreadBadge}>{dmUnread > 99 ? "99+" : dmUnread}</span>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      {/* ── Main Area ── */}
      <main style={S.main}>
        {/* Channel header */}
        <div style={S.channelHeader}>
          <div style={S.channelHeaderLeft}>
            {activeDmPerson ? (
              <>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: avatarGradient(activeDmPerson.name),
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0,
                }}>
                  {getInitials(activeDmPerson.name)}
                </div>
                <div>
                  <h2 style={S.channelHeaderName}>{activeDmPerson.name}</h2>
                  {activeDmPerson.role && (
                    <div style={{ fontSize: 11, color: C.textDim, marginTop: 1 }}>
                      {activeDmPerson.role}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <span style={S.channelHashIcon}>#</span>
                <h2 style={S.channelHeaderName}>{activeChannel}</h2>
              </>
            )}
          </div>
          <span style={S.channelHeaderMeta}>
            {activeDmPerson ? t('teamChat.directMessage') : t('teamChat.team')}
          </span>
          {activeDmPerson && (
            <button onClick={() => { setActiveDmPerson(null); setActiveChannel("general"); markVisited("general"); }}
              style={{ background: "transparent", border: `1px solid ${C.border}`,
                borderRadius: 6, color: C.textDim, padding: "4px 10px",
                cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
              ← {t('teamChat.backToChannels')}
            </button>
          )}
        </div>

        {/* #25: Case thread filter bar */}
        {caseFilter && (
          <div style={{ padding: "6px 16px", background: "rgba(167,139,250,0.08)", borderBottom: `1px solid rgba(167,139,250,0.2)`, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, color: "#A78BFA", fontFamily: "'DM Mono',monospace" }}>📁 Filtering: case "{caseFilter}"</span>
            <span style={{ fontSize: 11, color: "#A78BFA" }}>({filteredMessages.length} messages)</span>
            <button onClick={() => setCaseFilter("")} style={{ background: "none", border: "none", color: "#A78BFA", cursor: "pointer", fontSize: 12, marginLeft: "auto" }}>✕ Clear filter</button>
          </div>
        )}

        {/* Messages */}
        <div style={S.messageArea}>
          {loading ? (
            <div style={S.loadingWrap}>
              <span style={{ opacity: 0.5 }}>&#9679;&#9679;&#9679;</span>
              {t('teamChat.loadingMessages')}
            </div>
          ) : messages.length === 0 ? (
            <div style={S.emptyState}>
              <div style={S.emptyIcon}>💬</div>
              <p style={S.emptyText}>{t('teamChat.noMessagesYet')}</p>
            </div>
          ) : (
            grouped.map((item) => {
              if (item.type === "separator") {
                return (
                  <div key={item.key} style={S.separator}>
                    <div style={S.separatorLine} />
                    <span style={S.separatorLabel}>
                    {item.label === "Today" ? t('teamChat.today') : item.label === "Yesterday" ? t('teamChat.yesterday') : item.label}
                  </span>
                    <div style={S.separatorLine} />
                  </div>
                );
              }
              const isMine = item.user_id === myId;
              const initials = item.sender_initials || getInitials(item.sender_name);
              return (
                <div key={item.key} style={S.msgRow(isMine)}>
                  <div style={S.avatar(item.sender_name)}>{initials}</div>
                  <div style={S.msgBubbleWrap(isMine)}>
                    <div style={S.msgMeta(isMine)}>
                      <span style={S.msgSender}>{isMine ? t('teamChat.you') : item.sender_name}</span>
                      <span style={S.msgTime}>{formatTime(item.created_at)}</span>
                    </div>
                    <div style={S.msgBubble(isMine)}>{renderMessageWithMentions(item.message)}</div>
                    {/* #25: Case tag badge — clickable to filter or navigate */}
                    {item.case_id && (
                      <div style={{
                        marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4,
                        background: "rgba(167,139,250,0.15)", border: "1px solid rgba(167,139,250,0.4)",
                        borderRadius: 4, padding: "2px 8px", fontSize: 10, color: "#A78BFA",
                        fontFamily: "'DM Mono',monospace", cursor: "pointer",
                      }}
                        onClick={() => setCaseFilter(f => f === item.case_id ? "" : item.case_id)}
                        title="Click to filter by this case · Double-click to open case"
                        onDoubleClick={() => onNavigate && onNavigate("cases")}
                      >
                        📁 Case: {item.case_id} {caseFilter === item.case_id ? "✕" : ""}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div style={S.inputArea}>
          <div style={S.inputRow}>
            {/* Emoji button + popover */}
            <div style={{ position: "relative" }} ref={emojiRef}>
              <button
                style={S.emojiBtn}
                title={t('teamChat.emojiButtonTitle')}
                onClick={() => setShowEmoji((v) => !v)}
                onMouseEnter={(e) => (e.currentTarget.style.color = C.textMid)}
                onMouseLeave={(e) => (e.currentTarget.style.color = C.textDim)}
              >
                😊
              </button>
              {showEmoji && (
                <div style={S.emojiPopover}>
                  {EMOJI_LIST.map((em) => (
                    <button
                      key={em}
                      style={S.emojiOption}
                      title={em}
                      onClick={() => {
                        if (inputText.length < CHAR_LIMIT) {
                          setInputText((t) => t + em);
                        }
                        setShowEmoji(false);
                        textareaRef.current?.focus();
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = C.border)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      {em}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div style={{ position: "relative", flex: 1 }}>
              <textarea
                ref={textareaRef}
                style={S.textarea}
                placeholder={activeDmPerson ? t('teamChat.messagePlaceholderDm', { name: activeDmPerson.name }) : t('teamChat.messagePlaceholderChannel', { channel: activeChannel })}
                value={inputText}
                maxLength={CHAR_LIMIT}
                rows={1}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
              />

              {/* @mention suggestions dropdown */}
              {showMentions && mentionSuggestions.length > 0 && (
                <div
                  ref={mentionRef}
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: 0,
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    maxHeight: 200,
                    overflowY: "auto",
                    zIndex: 1000,
                    minWidth: 200,
                    marginBottom: 4,
                  }}
                >
                  {mentionSuggestions.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => insertMention(member)}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "8px 12px",
                        background: "transparent",
                        border: "none",
                        textAlign: "left",
                        cursor: "pointer",
                        borderBottom: `1px solid ${C.border}`,
                        color: C.text,
                        fontSize: 12,
                        fontFamily: "'DM Mono', monospace",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = C.accentGlow)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span style={{ fontWeight: 600 }}>@{member.name}</span>
                      <span style={{ color: C.textDim, marginLeft: 8, fontSize: 10 }}>
                        {member.email}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              style={S.sendBtn(!inputText.trim() || sending)}
              disabled={!inputText.trim() || sending}
              onClick={handleSend}
              title={t('teamChat.sendButtonTitle')}
            >
              {/* Send arrow icon */}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M1 7L13 1L7.5 7L13 13L1 7Z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div style={S.inputMeta}>
            <span style={S.inputHint}>{t('teamChat.inputHint')}</span>
            <span style={S.charCounter(charsLeft < 200)}>
              {charsLeft < 500 ? t('teamChat.charsRemaining', { count: charsLeft }) : ""}
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}
